<template>
  <!-- Row: Chart -->
  <div
    class="row f-row"
    :style="{
      height: controlsSpace
        ? 'calc(100% - 36px)!important'
        : 'calc(100% - 22px)!important',
    }"
  >
    <div class="col col-12 text-center progress-div" v-if="showLoading">
      <QSLoading :isLoading="showLoading" :size="35" :colorTheme="colorTheme" />
    </div>
    <div
      class="col col-12 text-center"
      v-else-if="candleData.length === 0"
      style="position: absolute; left: 0; top: 47.5%;"
    >
      {{ zeroRequests ? "Get data" : "No Data Available" }}
    </div>
    <div class="col col-12" style="height:100%; width:100%">
      <div
        class="h-100 d-flex flex-column"
        id="candles-div"
        style="width: 100%;"
      >
        <svg id="svg-candles"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart } from "@/_core/charts/D3Chart";
import { D3HoverHistorical } from "@/_core/charts/D3ChartAnalyticsHover";
import {
  candleAlpha,
  currencyFormat,
  dnCandles,
  dnCandlesAlpha,
  textinactive,
  tFormat,
} from "@/_core/_helpers/_parametric_tools";
import { format, select } from "d3";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "HistoricalChart",
  props: {
    candleData: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    controlsSpace: { required: false, type: Boolean, default: true },
    dateFormat: { required: true },
    includeLine: { required: false, type: Boolean, default: false },
    minMaxYAxis: { required: true },
    renderChartSignal: { required: true },
    showLoading: { required: true },
    showOHL: { required: false, type: Boolean, default: true },
    title: { required: true },
    zeroRequests: { required: true, type: Boolean, default: true },
    isZoomY: { required: false, default: false },
    showSimulations: { required: false, default: false },
  },
  components: { QSLoading },
  data() {
    return {
      // ========== Global Vars ===============
      tFormat: tFormat,
      currencyFormat: format(currencyFormat),

      // ========== Data =============
      chartName: "HIST",
      chartBodyTA: null,
      D3Chart: null,
      D3HoverHistorical: null,
      svgNode: null,
    };
  },
  created() {
    // ===== create initial charts instances =====
    this.D3Chart = new D3Chart("candles-div", "svg-candles");
    this.D3HoverHistorical = new D3HoverHistorical();
  },
  mounted() {
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
    this.callWidth();
  },
  watch: {
    colorTheme() {
      this.callWidth();
    },
    renderChartSignal() {
      if (this.candleData.length > 0) {
        this.createHistoricalChart();
      } else {
        this.deleteChart();
      }
    },
    showLoading(val) {
      if (this.candleData.length > 0 && !val) {
        this.createHistoricalChart();
      } else {
        this.deleteChart();
      }
    },
    candleData() {
      if (this.candleData.length > 0 && !this.showLoading) {
        this.createHistoricalChart();
      } else {
        this.deleteChart();
      }
    },
  },
  methods: {
    deleteChart() {
      select("#candles-div")
        .selectAll(".tooltip")
        .remove();
      select("#candles-div")
        .select("svg > *")
        .remove();
    },
    createHistoricalChart() {
      if (
        this.candleData.length === 0 ||
        !isFinite(this.minMaxYAxis[0]) ||
        !isFinite(this.minMaxYAxis[1])
      ) {
        return;
      }
      // === set SVG canvas
      this.svgNode = this.D3Chart.setSVG(
        0.5,
        5,
        0.5,
        1,
        0.5,
        20,
        0.5,
        55,
        true,
      );
      if (this.svgNode.height === 0 || this.svgNode.width === 0) {
        return;
      }
      // === set axis
      this.svgNode = this.D3Chart.setAxis(
        this.svgNode,
        this.candleData,
        this.minMaxYAxis,
        this.dateFormat,
        60,
        8,
        false,
        [],
        true,
        true,
        false,
        this.currencyFormat,
        true,
      ); // ==== zoom setup
      this.chartBodyTA = this.svgNode
        .append("g")
        .attr("class", "chartBodyHC")
        .attr("clip-path", `url(#clipTA${this.chartName})`);
      this.svgNode
        .append("defs")
        .append("clipPath")
        .attr("id", "clipTA" + this.chartName)
        .append("rect")
        .attr("width", this.svgNode.width)
        .attr("height", this.svgNode.height);

      if (this.includeLine === true) {
        // === vertical line at today
        this.chartBodyTA
          .append("line")
          .attr("stroke-width", 1)
          .attr("stroke-opacity", 0.3)
          .attr("stroke", dnCandles(this.colorTheme))
          .attr("x1", this.svgNode.width + 1)
          .attr("y1", 0)
          .attr("x2", this.svgNode.width + 1)
          .attr("y2", this.svgNode.height);
      }
      // === candles
      this.svgNode = this.D3Chart.candles(
        this.svgNode,
        this.candleData,
        textinactive(this.colorTheme),
        dnCandlesAlpha(this.colorTheme),
        "TA",
        candleAlpha(this.colorTheme),
        candleAlpha(this.colorTheme),
        null,
        null,
        1,
        false,
        1 / 22,
        [],
        false,
        "id",
        false,
        "stick",
        "stick2",
        this.chartBodyTA,
      );

      const candleHoverConfiguration = [
        {
          data: this.candleData,
          customNameBars: ["candle", "stick", "stick2"],
        },
      ];

      const figuresElemsHover = [
        { type: "candlesOld", data: candleHoverConfiguration },
      ];

      if (this.showSimulations) {
        figuresElemsHover.push({
          type: "lineZeroY",
        });
      }
      // === custom hover effects and info
      this.D3HoverHistorical.hoverFct(
        this.svgNode,
        this.candleData,
        "TA",
        this.currencyFormat,
        this.dateFormat,
        this.title,
        this.currencyFormat,
        this.showOHL,
        this.colorTheme,
        this.isZoomY,
        figuresElemsHover,
        " ",
        this.candleData,
        false,
        this,
        true,
      );
    },
    callWidth() {
      if (this.candleData.length > 0) {
        this.createHistoricalChart();
      }
    },
  },
};
</script>
